import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import { ILegalClientGroup } from '../Interfaces/ILegalClientGroup';
import { CustomeTextField } from '../generic/CustomeTextField';
import { ISearchOption } from '../Interfaces/ISearchOption';
export interface ISearchObject {
  searchTerm: string;
  searchFilter: string[];
  groupFilter: string[];
  lastLoginFilter: null | boolean;
  syncWithArnoTop: null | boolean;
  loginPossible: null | boolean;
  isReceivingEmailEnabled: null | boolean;
  searchType: 'starts_with' | 'includes' | 'exact_match';
  /** Optionaler Serverfilter auf den Mandantentyp (z. B. 2 = juristische Person). */
  idLegalClientType?: null | number;
}

export const getSearchUri = (
  currentPage: number,
  currentPageSize: number,
  currentSearchObject: ISearchObject
) => {
  let searchQuery = '';

  for (let i = 0; i < currentSearchObject.searchFilter.length; i++) {
    searchQuery += `${currentSearchObject.searchFilter[i]}:${currentSearchObject.searchTerm};`;
  }

  let addFilter = '';

  if (currentSearchObject.loginPossible !== null) {
    addFilter += `&loginPossible=${currentSearchObject.loginPossible}`;
  }
  if (currentSearchObject.isReceivingEmailEnabled !== null) {
    addFilter += `&isReceivingEmailEnabled=${currentSearchObject.isReceivingEmailEnabled}`;
  }
  if (currentSearchObject.syncWithArnoTop !== null) {
    addFilter += `&syncWithArnoTop=${currentSearchObject.syncWithArnoTop}`;
  }
  if (
    currentSearchObject.idLegalClientType !== null &&
    currentSearchObject.idLegalClientType !== undefined
  ) {
    addFilter += `&idLegalClientType=${currentSearchObject.idLegalClientType}`;
  }

  return currentSearchObject.searchTerm === ''
    ? `page=${currentPage + 1}&rowsPerPage=${currentPageSize}${addFilter}`
    : `search=${searchQuery}&searchtype=${currentSearchObject.searchType}&page=${currentPage + 1}&rowsPerPage=${currentPageSize}${addFilter}`;
};

export const legalClientsPossibleSearchOptions: ISearchOption[] = [
  { SearchOptionName: 'Nr.', SearchOptionKey: 'idLegalClient' },
  {
    SearchOptionName: 'Namen Nr. / ArnoTop-Nummber',
    SearchOptionKey: 'idArnoTop',
  },
  { SearchOptionName: 'Vorname', SearchOptionKey: 'FirstName' },
  {
    SearchOptionName: 'Nachname / Firma',
    SearchOptionKey: 'LastName',
  },
  { SearchOptionName: 'E-Mail', SearchOptionKey: 'Email' },
];
type SearchField = {
  attr: keyof ISearchObject;
  title: string;
};
const fields: SearchField[] = [
  { attr: 'lastLoginFilter', title: 'Bereits Eingeloggt' },
  { attr: 'syncWithArnoTop', title: 'Aktiv: Synchronisation ArnoTop' },
  { attr: 'loginPossible', title: 'Aktiv: Login' },
  { attr: 'isReceivingEmailEnabled', title: 'Aktiv: E-Mails-Empfang' },
];

interface IProps {
  groups: ILegalClientGroup[];
  search: ISearchObject;
  setSearch: React.Dispatch<React.SetStateAction<ISearchObject>>;
}

export const ClientsSearch: React.FC<IProps> = ({
  groups,
  search,
  setSearch,
}) => {
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 270,
        width: 300,
      },
    },
  };

  const MenuPropsSmall = {
    PaperProps: {
      style: {
        maxHeight: 270,
        width: 175,
      },
    },
  };

  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  //

  const [filterAll, setFilterAll] = useState(true);

  //

  const [loginDateRange, setLoginDateRange] = useState({
    start: new Date('2024-01-01'),
    end: new Date(),
  });

  const [loginFilter, setLoginFilter] = useState<'ALL' | 'TRUE' | 'FALSE'>(
    'ALL'
  );
  const [emailFilter, setEmailFilter] = useState<'ALL' | 'TRUE' | 'FALSE'>(
    'ALL'
  );

  useEffect(() => {
    console.log('search.searchFilter');
    console.log(search.searchFilter);
  }, [search.searchFilter]);

  const activeGroupChips = useMemo(() => {
    return search.groupFilter.map(id => {
      const group = groups.find(g => String(g.idLegalClientGroup) === id);
      return {
        label: group?.Name ?? id,
        onDelete: () =>
          setSearch(prev => ({
            ...prev,
            groupFilter: prev.groupFilter.filter(g => g !== id),
          })),
      };
    });
  }, [search.groupFilter, groups]);

  return (
    <>
      <Box mt={6} mb={2}>
        <CustomeTextField
          attr='searchTerm'
          label='Suche'
          object={search}
          setObject={setSearch}
          type='string'
        />

        {/* Advanced Search Toggle */}
        <Box
          mt={0.5}
          ml={0.5}
          display='flex'
          alignItems='center'
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => {
            setShowAdvancedSearch(prev => {
              const next = !prev;
              if (!next) {
                setSearch({
                  isReceivingEmailEnabled: null,
                  groupFilter: [],
                  lastLoginFilter: null,
                  loginPossible: null,
                  searchFilter: legalClientsPossibleSearchOptions.map(
                    item => item.SearchOptionKey
                  ),
                  searchTerm: '',
                  syncWithArnoTop: null,
                  searchType: 'includes',
                });
              }
              return next;
            });
          }}
        >
          <Typography variant='body2'>Erweiterte Suche</Typography>
          <IconButton size='small'>
            {showAdvancedSearch ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Box>

        {/* Advanced Search Panel */}
        <Collapse in={showAdvancedSearch} timeout='auto' unmountOnExit>
          <Box
            ml={0.5}
            mt={2}
            sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
          >
            {/* Search in fields */}
            <FormControl variant='standard' sx={{ flex: '1 1 300px' }}>
              <InputLabel>Suchfelder</InputLabel>
              <Select
                multiple
                MenuProps={MenuProps}
                value={
                  search.searchFilter.length > 0 ? search.searchFilter : []
                }
                onChange={({ target }) => {
                  setSearch(prev => ({
                    ...prev,
                    searchFilter: (target.value as string[]) || [],
                  }));
                }}
                renderValue={selected => {
                  if (
                    selected.length === legalClientsPossibleSearchOptions.length
                  ) {
                    return 'Alle Felder';
                  }

                  return selected
                    .map(
                      key =>
                        legalClientsPossibleSearchOptions.find(
                          opt => opt.SearchOptionKey === key
                        )?.SearchOptionName
                    )
                    .join(', ');
                }}
              >
                {legalClientsPossibleSearchOptions.map(opt => (
                  <MenuItem
                    key={opt.SearchOptionKey}
                    value={opt.SearchOptionKey}
                  >
                    {opt.SearchOptionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* NEW CLEAN GROUP FILTER */}
            <FormControl variant='standard' sx={{ flex: '1 1 300px' }}>
              <InputLabel>Gruppe(n)</InputLabel>
              <Select
                multiple
                MenuProps={MenuProps}
                value={search.groupFilter}
                onChange={e => {
                  setSearch(prev => ({
                    ...prev,
                    groupFilter: e.target.value as string[],
                  }));
                }}
                renderValue={selected => {
                  if (selected.length === 0) return 'Alle Gruppen';
                  return groups
                    .filter(g =>
                      selected.includes(String(g.idLegalClientGroup))
                    )
                    .map(g => g.Name)
                    .join(', ');
                }}
              >
                {groups.map(g => (
                  <MenuItem
                    key={g.idLegalClientGroup}
                    value={String(g.idLegalClientGroup)}
                  >
                    <Box display='flex' alignItems='center' gap={1}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: g.ColorHex,
                          border: '1px solid #ccc',
                        }}
                      />
                      {g.Name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {fields.map(item => (
              <FormControl variant='standard' sx={{ flex: '1 1 300px' }}>
                <InputLabel>{item.title}</InputLabel>
                <Select
                  MenuProps={MenuProps}
                  value={
                    search[item.attr] === null
                      ? 'ALL'
                      : search[item.attr] === true
                        ? 'TRUE'
                        : 'FALSE'
                  }
                  onChange={e => {
                    setSearch(prev => ({
                      ...prev,
                      [item.attr]:
                        e.target.value === 'ALL'
                          ? null
                          : e.target.value === 'TRUE',
                    }));
                  }}
                >
                  <MenuItem value='ALL'>Kein Filter</MenuItem>
                  <MenuItem value='TRUE'>Ja</MenuItem>
                  <MenuItem value='FALSE'>Nein</MenuItem>
                </Select>
              </FormControl>
            ))}

            {/* GROUP FILTER CHIPS */}
            {activeGroupChips.length > 0 && (
              <Box mt={1}>
                <Typography variant='overline'>Gruppen</Typography>
                <Box display='flex' gap={1} flexWrap='wrap'>
                  {activeGroupChips.map((chip, idx) => (
                    <Chip
                      key={idx}
                      label={chip.label}
                      onDelete={chip.onDelete}
                      variant='outlined'
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  );
};
